import {FC, PropsWithChildren, ReactNode, createContext, useEffect, useRef} from 'react';
import {AxiosError} from 'axios';
import {ToastOptions, toast} from 'react-toastify';
import {useRecoilState} from 'recoil';
import {eventbus} from '../../shared/lib/eventbus';
import {authState} from '../../store/auth/atoms';
import {ToastHelpers} from '../../shared/helpers/toastHelpers';

type EventBusMap = {
  error: (args: [error: AxiosError | Error | ReactNode, config?: ToastOptions, withoutBackdrop?: boolean]) => void;
  isOnline: (args: [isOnline: boolean, config?: ToastOptions]) => void;
  info: (args: [message: string, config?: ToastOptions]) => void;
};

type Bus = ReturnType<typeof eventbus>;

export const EventBusContext = createContext<{bus: Bus | null}>({bus: null});

const EventBus: FC<PropsWithChildren> = ({children}) => {
  const {current: bus} = useRef(eventbus<EventBusMap>());
  const [{isLoggedIn}, setAuthState] = useRecoilState(authState);

  useEffect(() => {
    const unsubError = bus.on('error', ([error, config, withoutBackdrop]) => {
      if (error instanceof AxiosError && !window.navigator.onLine) {
        bus.emit('isOnline', [window.navigator.onLine]);
        return;
      }

      if (error instanceof AxiosError && error.response?.status === 401) {
        isLoggedIn && setAuthState((prev) => ({...prev, user: null, isLoggedIn: false}));
      }

      ToastHelpers.error(error, config, withoutBackdrop);
    });

    const unsubIsOnline = bus.on('isOnline', ([isOnline, config]) => {
      if (isOnline) {
        toast.dismiss('offline');
        toast.success('Подключение к Интернету доступно', {
          containerId: 'default',
          autoClose: false,
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: false,
          toastId: 'online',
          ...config,
        });
      } else {
        toast.dismiss('online');
        toast.error('Нет доступа к Интернету. Проверьте подключение', {
          containerId: 'default',
          autoClose: false,
          hideProgressBar: true,
          pauseOnHover: true,
          draggable: false,
          toastId: 'offline',
          ...config,
        });
      }
    });

    const unsubInfo = bus.on('info', ([message, config]) => {
      toast.info(message, {...config, containerId: 'default'});
    });

    return () => {
      unsubError();
      unsubIsOnline();
      unsubInfo();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return <EventBusContext.Provider value={{bus}}>{children}</EventBusContext.Provider>;
};

export default EventBus;
